import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CalendarRange, Pencil, Plus } from "lucide-react";

interface BookingRecord {
  id: string;
  userId: string;
  serviceItemId: string;
  taskId: string | null;
  bookingDate: string;
  minutes: number;
  bookingType: "service" | "task" | "internal" | "leave";
  note: string | null;
  userName: string;
  userEmail: string;
  serviceItemName: string;
  taskTitle: string | null;
}

interface BookingOptionsResponse {
  staff: Array<{ id: string; name: string; role: string }>;
  serviceItems: Array<{ id: string; name: string; requiresTask: boolean }>;
  tasks: Array<{ id: string; title: string; projectId: string; serviceItemId: string | null; status: string }>;
}

interface BookingFormState {
  userId: string;
  serviceItemId: string;
  taskId: string | null;
  bookingDate: string;
  minutes: string;
  bookingType: BookingRecord["bookingType"];
  note: string;
}

const emptyBookingForm: BookingFormState = {
  userId: "",
  serviceItemId: "",
  taskId: null,
  bookingDate: "",
  minutes: "60",
  bookingType: "service",
  note: "",
};

function toDateInput(value: string) {
  return value.slice(0, 10);
}

export default function BookingsPage() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState<BookingRecord | null>(null);
  const [form, setForm] = useState<BookingFormState>(emptyBookingForm);
  const [selectedUserId, setSelectedUserId] = useState("all");
  const [search, setSearch] = useState("");

  const bookingQueryString = new URLSearchParams(
    selectedUserId !== "all" ? { userId: selectedUserId } : {},
  ).toString();

  const { data: bookings, isLoading } = useQuery<BookingRecord[]>({
    queryKey: [`/api/bookings${bookingQueryString ? `?${bookingQueryString}` : ""}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: options } = useQuery<BookingOptionsResponse>({
    queryKey: ["/api/bookings/meta/options"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const filteredBookings = useMemo(() => {
    if (!bookings) return [];
    if (!search.trim()) return bookings;
    const term = search.toLowerCase();
    return bookings.filter(
      (booking) =>
        booking.userName.toLowerCase().includes(term) ||
        booking.serviceItemName.toLowerCase().includes(term) ||
        booking.taskTitle?.toLowerCase().includes(term),
    );
  }, [bookings, search]);

  const filteredTasks = useMemo(() => {
    if (!options) return [];
    if (!form.serviceItemId) return options.tasks;
    return options.tasks.filter(
      (task) => !task.serviceItemId || task.serviceItemId === form.serviceItemId,
    );
  }, [options, form.serviceItemId]);

  const createMutation = useMutation({
    mutationFn: async (payload: BookingFormState) => {
      const response = await apiRequest("POST", "/api/bookings", {
        ...payload,
        taskId: payload.taskId,
        minutes: Number(payload.minutes),
        note: payload.note || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/bookings"),
      });
      setDialogOpen(false);
      setEditingBooking(null);
      setForm(emptyBookingForm);
      toast({ title: "Booking created" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to create booking",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: BookingFormState) => {
      const response = await apiRequest("PATCH", `/api/bookings/${editingBooking?.id}`, {
        ...payload,
        taskId: payload.taskId,
        minutes: Number(payload.minutes),
        note: payload.note || null,
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/bookings"),
      });
      setDialogOpen(false);
      setEditingBooking(null);
      setForm(emptyBookingForm);
      toast({ title: "Booking updated" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update booking",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const columns: ColumnDef<BookingRecord>[] = [
    {
      key: "date",
      header: "Date",
      cell: (booking) => toDateInput(booking.bookingDate),
    },
    {
      key: "staff",
      header: "Staff",
      cell: (booking) => (
        <div>
          <p className="font-medium">{booking.userName}</p>
          <p className="text-xs text-muted-foreground">{booking.userEmail}</p>
        </div>
      ),
    },
    {
      key: "assignment",
      header: "Assignment",
      cell: (booking) => (
        <div className="space-y-1">
          <p className="font-medium">{booking.serviceItemName}</p>
          <p className="text-xs text-muted-foreground">
            {booking.taskTitle ? `Task: ${booking.taskTitle}` : "Service-only booking"}
          </p>
        </div>
      ),
    },
    {
      key: "minutes",
      header: "Minutes",
      cell: (booking) => `${booking.minutes} min`,
    },
    {
      key: "type",
      header: "Type",
      cell: (booking) => <Badge variant="outline" className="capitalize">{booking.bookingType}</Badge>,
    },
    {
      key: "actions",
      header: "",
      cell: (booking) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={(event) => {
            event.stopPropagation();
            setEditingBooking(booking);
            setForm({
              userId: booking.userId,
              serviceItemId: booking.serviceItemId,
              taskId: booking.taskId,
              bookingDate: toDateInput(booking.bookingDate),
              minutes: String(booking.minutes),
              bookingType: booking.bookingType,
              note: booking.note ?? "",
            });
            setDialogOpen(true);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      ),
      className: "w-[64px]",
    },
  ];

  function openCreate() {
    setEditingBooking(null);
    setForm({
      ...emptyBookingForm,
      userId: options?.staff[0]?.id ?? "",
      serviceItemId: options?.serviceItems[0]?.id ?? "",
      bookingDate: new Date().toISOString().slice(0, 10),
    });
    setDialogOpen(true);
  }

  function handleSubmit() {
    if (editingBooking) {
      updateMutation.mutate(form);
      return;
    }
    createMutation.mutate(form);
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Bookings"
        description="Plan recurring service time or reserve capacity against explicit delivery tasks."
        action={
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add booking
          </Button>
        }
      />

      <DataTable
        columns={columns}
        data={filteredBookings}
        isLoading={isLoading}
        rowKey={(booking) => booking.id}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search bookings..."
        filterSlot={
          <Select value={selectedUserId} onValueChange={setSelectedUserId}>
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="Filter by staff" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All staff</SelectItem>
              {options?.staff.map((staff) => (
                <SelectItem key={staff.id} value={staff.id}>
                  {staff.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        emptyIcon={CalendarRange}
        emptyTitle="No bookings"
        emptyDescription="Create bookings for retained client service time or specific project tasks."
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[620px]">
          <DialogHeader>
            <DialogTitle>{editingBooking ? "Edit booking" : "Create booking"}</DialogTitle>
            <DialogDescription>
              A booking can target a service item on its own, or a task plus service item together.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Staff</Label>
              <Select
                value={form.userId || undefined}
                onValueChange={(value) => setForm((current) => ({ ...current, userId: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select staff member" />
                </SelectTrigger>
                <SelectContent>
                  {options?.staff.map((staff) => (
                    <SelectItem key={staff.id} value={staff.id}>
                      {staff.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Booking type</Label>
              <Select
                value={form.bookingType}
                onValueChange={(value: BookingRecord["bookingType"]) =>
                  setForm((current) => ({ ...current, bookingType: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="task">Task</SelectItem>
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="leave">Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Service item</Label>
              <Select
                value={form.serviceItemId || undefined}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    serviceItemId: value,
                    taskId:
                      current.taskId &&
                      filteredTasks.some((task) => task.id === current.taskId)
                        ? current.taskId
                        : null,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select service item" />
                </SelectTrigger>
                <SelectContent>
                  {options?.serviceItems.map((serviceItem) => (
                    <SelectItem key={serviceItem.id} value={serviceItem.id}>
                      {serviceItem.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Task</Label>
              <Select
                value={form.taskId ?? "none"}
                onValueChange={(value) =>
                  setForm((current) => ({
                    ...current,
                    taskId: value === "none" ? null : value,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Optional task" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Service-only booking</SelectItem>
                  {filteredTasks.map((task) => (
                    <SelectItem key={task.id} value={task.id}>
                      {task.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="booking-date">Booking date</Label>
              <Input
                id="booking-date"
                type="date"
                value={form.bookingDate}
                onChange={(event) =>
                  setForm((current) => ({ ...current, bookingDate: event.target.value }))
                }
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="booking-minutes">Minutes</Label>
              <Input
                id="booking-minutes"
                type="number"
                min="1"
                value={form.minutes}
                onChange={(event) => setForm((current) => ({ ...current, minutes: event.target.value }))}
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="booking-note">Note</Label>
              <Textarea
                id="booking-note"
                rows={4}
                value={form.note}
                onChange={(event) => setForm((current) => ({ ...current, note: event.target.value }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={
                !form.userId ||
                !form.serviceItemId ||
                !form.bookingDate ||
                !form.minutes ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {editingBooking ? "Save booking" : "Create booking"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
